import { useAnimate, motion } from "framer-motion";
import React, {
    MouseEventHandler,
    ReactElement,
    forwardRef,
    useEffect,
    useState
} from "react";
import { useTranslation } from "react-i18next";

import * as svg from "./../svg";
import { DeleteLineItemDialog } from "./DeleteLineItemDialog";
import { PromoPrice } from "./Promotion";
import { ActionKind } from "../../types/Action";
import { GroupPosition, SinglePosition } from "../../types/Cart";
import {
    COUPON_ARTIKELTYP,
    isDiscounted,
    isUnappliedCoupon
} from "../../utils/Cart";
import { translate } from "../../utils/translations";
import { useConfig } from "../ConfigProvider";
import { usePriceFormat } from "../PriceFormatProvider";
import { useRootState, RootStateKind } from "../state/StateProvider";

const LineItemActionButton = forwardRef<
    HTMLButtonElement,
    {
        image: ReactElement;
        disabled?: boolean;
        onClick?: MouseEventHandler<HTMLButtonElement>;
        testId?: string;
    }
>(function LineItemActionButton({ image, disabled, onClick, testId }, ref) {
    return (
        <button
            data-testid={testId}
            ref={ref}
            disabled={disabled}
            onClick={onClick}
            className="grid h-7 w-7 shrink-0 place-content-center rounded border-2 border-black bg-white disabled:opacity-30"
        >
            {image}
        </button>
    );
});

const SingleProductDetails = ({ position }: { position: SinglePosition }) => {
    const { i18n } = useTranslation();
    const format = usePriceFormat();
    return (
        <div className="flex grow flex-col">
            <div className="grid grid-cols-[1fr_auto] items-center gap-x-4 gap-y-2">
                <div className="flex">
                    <div className="flex-1">
                        <div className="flex-1 font-semibold capitalize">
                            {position.product.name.toLocaleLowerCase()}
                        </div>
                        {position.addOnOptions &&
                            position.addOnOptions.length > 0 && (
                                <div className="text-xs">
                                    {position.addOnOptions
                                        ?.map((option) =>
                                            translate(option.name, i18n)
                                        )
                                        .join(", ")}
                                </div>
                            )}
                    </div>
                    {position.product.isAgeRestricted && (
                        <div className="px-1">{svg.eighteenPlusBadge}</div>
                    )}
                </div>
                <div className="flex justify-self-end whitespace-nowrap text-xs leading-5">
                    {isUnappliedCoupon(position) ? (
                        <div
                            data-testid="unappliedCouponIcon"
                            className="text-primary-500"
                        >
                            {svg.warning}
                        </div>
                    ) : !isDiscounted(position) ? (
                        <div className="font-semibold">
                            {format(position.linePrice)}
                        </div>
                    ) : (
                        <PromoPrice
                            originalPrice={format(position.grossPrice)}
                            discountedPrice={format(position.linePrice)}
                        />
                    )}
                </div>
                {position.depositArticle && (
                    <>
                        <div className="text-xs leading-5">
                            {position.depositArticle.name}
                        </div>
                        <div className="justify-self-end whitespace-nowrap text-xs">
                            {format(position.depositArticle.price)}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

const ProductGroupDetails = ({ position }: { position: GroupPosition }) => {
    const { i18n } = useTranslation();
    const format = usePriceFormat();
    return (
        <div className="flex grow flex-col">
            <div className="grid grid-cols-[1fr_auto] items-center gap-x-4 gap-y-2">
                <div>
                    <div className="flex-1 font-semibold capitalize">
                        {position.name.toLocaleLowerCase()}
                    </div>
                    {position.lineItems.map((lineItem, i) => (
                        <div key={`group-${lineItem.id}-${i}`} className="flex">
                            <div className="flex flex-1 flex-col">
                                <div className="text-xs">
                                    {lineItem.product.name}
                                </div>
                                {lineItem.addOnOptions &&
                                    lineItem.addOnOptions.length > 0 && (
                                        <div className="text-[0.6rem]">
                                            {lineItem.addOnOptions
                                                .map((o) =>
                                                    translate(o.name, i18n)
                                                )
                                                .join(", ")}
                                        </div>
                                    )}
                            </div>
                            {lineItem.product.isAgeRestricted && (
                                <div className="px-1">
                                    {svg.eighteenPlusBadge}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
                <div className="flex justify-self-end whitespace-nowrap text-xs leading-5">
                    {position.grossPrice.value === position.price.value ? (
                        <div className="font-semibold">
                            {format(position.price)}
                        </div>
                    ) : (
                        <PromoPrice
                            originalPrice={format(position.grossPrice)}
                            discountedPrice={format(position.price)}
                        />
                    )}
                </div>
                {position.lineItems
                    .filter((li) => !!li.depositArticle)
                    .map((lineItem, i) => (
                        <React.Fragment
                            key={`group-${lineItem.id}-${
                                lineItem.depositArticle!.name
                            }-${i}`}
                        >
                            <div className="text-xs leading-5">
                                {lineItem.depositArticle!.name}
                            </div>
                            <div className="justify-self-end whitespace-nowrap text-xs leading-5">
                                {format(lineItem.depositArticle!.price)}
                            </div>
                        </React.Fragment>
                    ))}
            </div>
        </div>
    );
};

export const Product = ({
    position
}: {
    position: SinglePosition | GroupPosition;
}) => {
    const { dispatch } = useRootState(RootStateKind.Scan);
    const { tenant } = useConfig();

    const updateLineItem = (action: "increment" | "decrement") => {
        switch (position.kind) {
            case "single":
                dispatch({
                    kind: ActionKind.UpdateLineItem,
                    product: { id: position.id, action }
                });
                break;
            case "group":
                break;
        }
    };

    const deleteLineItem = () => {
        switch (position.kind) {
            case "single":
                dispatch({
                    kind: ActionKind.DeleteLineItem,
                    product: { id: position.id }
                });
                break;
            case "group":
                dispatch({
                    kind: ActionKind.DeleteCombo,
                    position
                });
                break;
        }
    };

    const [showDeleteLineItemDialog, setShowDeleteLineItemDialog] =
        useState(false);

    const [incrementRef, animateIncrement] = useAnimate();
    const [decrementRef, animateDecrement] = useAnimate();
    const [lineItemRef, animateLineItem] = useAnimate();
    const [containerRef] = useAnimate();

    // tailwind classnames are not directly supported by the animation package
    const highlight = (function () {
        switch (tenant) {
            case "avec":
                return "#E5005B";
            case "kkiosk":
                return "#00C5F5";
            case "oxxo":
                return "#CF322B";
            case "pressAndBooks":
                return "#FFBA00";
            default:
                return tenant satisfies never;
        }
    })();
    const background = "hsl(0 0 95%)";

    useEffect(() => {
        containerRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start"
        });

        animateLineItem(
            lineItemRef.current,
            {
                backgroundColor: [highlight, background]
            },
            {
                duration: 1,
                ease: "linear"
            }
        );
    }, [position.quantity]);

    const grid = (
        <>
            {showDeleteLineItemDialog && (
                <DeleteLineItemDialog
                    onClose={(result) => {
                        if (result) deleteLineItem();
                        setShowDeleteLineItemDialog(false);
                    }}
                />
            )}
            <div className="flex items-center">
                <LineItemActionButton
                    testId={
                        position.kind === "single"
                            ? `decrement-line-item-${position.product.barcode}`
                            : `decrement-position-${position.name}`
                    }
                    ref={decrementRef}
                    image={svg.minus}
                    disabled={
                        position.kind === "group" ||
                        position.product.artikeltyp === COUPON_ARTIKELTYP ||
                        position.isDeleted
                    }
                    onClick={() => {
                        animateDecrement(
                            decrementRef.current,
                            { scale: [1.15, 1] },
                            {
                                ease: "easeInOut"
                            }
                        );
                        const quantity = position.quantity - 1;
                        if (quantity < 1) {
                            setShowDeleteLineItemDialog(true);
                        } else {
                            updateLineItem("decrement");
                        }
                    }}
                />
                <div
                    className="w-10 text-center"
                    data-testid={
                        position.kind === "single"
                            ? `quantity-line-item-${position.product.barcode}`
                            : `quantity-position-${position.name}`
                    }
                >
                    {position.quantity}
                </div>
                <LineItemActionButton
                    testId={
                        position.kind === "single"
                            ? `increment-line-item-${position.product.barcode}`
                            : `increment-position-${position.name}`
                    }
                    ref={incrementRef}
                    image={svg.plus}
                    disabled={
                        position.kind === "group" ||
                        position.product.artikeltyp === COUPON_ARTIKELTYP ||
                        position.isDeleted ||
                        position.quantity >= 99
                    }
                    onClick={() => {
                        animateIncrement(
                            incrementRef.current,
                            { scale: [1.15, 1] },
                            {
                                ease: "easeOut"
                            }
                        );
                        updateLineItem("increment");
                    }}
                />
            </div>

            {position.kind === "single" && (
                <SingleProductDetails position={position} />
            )}
            {position.kind === "group" && (
                <ProductGroupDetails position={position} />
            )}

            <LineItemActionButton
                image={svg.trash}
                disabled={position.kind === "single" && position.isDeleted}
                onClick={() => setShowDeleteLineItemDialog(true)}
                testId={
                    position.kind === "single"
                        ? `delete-line-item-${position.product.barcode}`
                        : `delete-line-item-${position.name}`
                }
            />
        </>
    );

    return (
        <motion.div
            data-testid={
                position.kind === "single"
                    ? "lineItem".concat(position.product.barcode)
                    : "position".concat(position.name)
            }
            key={
                position.kind === "single"
                    ? `single-${position.id}`
                    : position.name
            }
            ref={containerRef}
            exit={{ height: 0, opacity: 0, zIndex: -1 }}
            className="last:pb-3"
        >
            <motion.div
                ref={lineItemRef}
                animate={{
                    transitionEnd: {
                        backgroundColor: background
                    }
                }}
                className={`mt-3 flex shrink-0 items-center gap-5 overflow-hidden rounded p-3 text-sm ${
                    position.kind === "single" && position.isDeleted
                        ? "relative opacity-30 before:absolute before:left-0 before:right-0 before:top-1/2 before:block before:border-2 before:border-b before:border-black"
                        : ""
                }`}
            >
                {grid}
            </motion.div>
        </motion.div>
    );
};
